import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme as useMuiTheme,
} from "@mui/material";
import {
  ChevronDown,
  ChevronRight,
  Clapperboard,
  Bell,
  CreditCard,
  Database,
  FileText,
  Gauge,
  Landmark,
  LayoutDashboard,
  Settings,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { ProjectSelector } from "../../../global/components/ProjectSelector";
import SidebarAssetList from "../../components/SidebarAssetList";
import { useProject } from "../../contexts/ProjectContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useTheme } from "../../contexts/ThemeContext";
const SIDEBAR_WIDTH = 275;

function Sidebar() {
  const { t } = useTranslation("common");
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();
  const location = useLocation();
  const { project, currentUserRole } = useProject();
  const { sidebarOpen, closeSidebar, isMobile } = useSidebar();

  const [bankingOpen, setBankingOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  // Check if Members menu should be shown
  const shouldShowMembersMenu = () => {
    // Always show for owners, producers, and co-producers (they can invite sub-users)
    if (["owner", "producer", "co_producer"].includes(currentUserRole))
      return true;

    // For viewers, show only if members_can_see_other_members is true (default)
    return project?.members_can_see_other_members !== false;
  };

  // Check if Banking menu should be shown (only for owners)
  const shouldShowBankingMenu = () => {
    return currentUserRole === "owner";
  };

  const isActivePage = (path) => {
    return location.pathname === path;
  };

  const handleBankingClick = () => {
    setBankingOpen(!bankingOpen);
  };

  const handleReportsClick = () => {
    setReportsOpen(!reportsOpen);
  };

  const SidebarContent = () => (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <ProjectSelector />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          px: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 120px)", // Adjust based on header height
        }}
      >
        <Box sx={{ flex: 1 }}>
          <List
            sx={{
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/"
                selected={isActivePage("/")}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LayoutDashboard
                    size={20}
                    color={colors.text.primary}
                    strokeWidth={1.5}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.dashboard")}
                  primaryTypographyProps={{
                    variant: "bodySm",
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/analytics"
                selected={isActivePage("/analytics")}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Gauge
                    size={20}
                    color={colors.text.primary}
                    strokeWidth={1.5}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.analytics")}
                  primaryTypographyProps={{
                    variant: "bodySm",
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/sources"
                selected={isActivePage("/sources")}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Database
                    size={20}
                    color={colors.text.primary}
                    strokeWidth={1.5}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.sources")}
                  primaryTypographyProps={{
                    variant: "bodySm",
                  }}
                />
              </ListItemButton>
            </ListItem>

            {shouldShowMembersMenu() && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/members"
                  selected={isActivePage("/members")}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    "&.Mui-selected": {
                      backgroundColor: "action.selected",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Users
                      size={20}
                      color={colors.text.primary}
                      strokeWidth={1.5}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("navigation.members")}
                    primaryTypographyProps={{
                      variant: "bodySm",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/assets"
                selected={isActivePage("/assets")}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Clapperboard
                    size={20}
                    color={colors.text.primary}
                    strokeWidth={1.5}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.assets")}
                  primaryTypographyProps={{
                    variant: "bodySm",
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Reports Section */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleReportsClick}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <FileText
                    size={20}
                    color={colors.text.primary}
                    strokeWidth={1.5}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.reports")}
                  primaryTypographyProps={{
                    variant: "bodySm",
                  }}
                />
                {reportsOpen ? (
                  <ChevronDown size={16} color={colors.text.secondary} />
                ) : (
                  <ChevronRight size={16} color={colors.text.secondary} />
                )}
              </ListItemButton>
            </ListItem>

            {/* Reports Sub-items */}
            <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/reports"
                    selected={isActivePage("/reports")}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      pl: 7,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <FileText
                        size={18}
                        color={colors.text.primary}
                        strokeWidth={1.5}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("navigation.reports")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to="/report-templates"
                    selected={isActivePage("/report-templates")}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      pl: 7,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <FileText
                        size={18}
                        color={colors.text.primary}
                        strokeWidth={1.5}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("navigation.templates")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/alerts"
                selected={isActivePage("/alerts")}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Bell
                    size={20}
                    color={colors.text.primary}
                    strokeWidth={1.5}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.alerts")}
                  primaryTypographyProps={{
                    variant: "bodySm",
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Banking Section - Only visible for owners */}
            {shouldShowBankingMenu() && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleBankingClick}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Landmark
                        size={20}
                        color={colors.text.primary}
                        strokeWidth={1.5}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("navigation.banking")}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                    />
                    {bankingOpen ? (
                      <ChevronDown size={16} color={colors.text.secondary} />
                    ) : (
                      <ChevronRight size={16} color={colors.text.secondary} />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Banking Sub-items */}
                <Collapse in={bankingOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/expenses"
                        selected={isActivePage("/expenses")}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          pl: 7,
                          "&.Mui-selected": {
                            backgroundColor: "action.selected",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Wallet
                            size={18}
                            color={colors.text.primary}
                            strokeWidth={1.5}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("navigation.expenses")}
                          primaryTypographyProps={{
                            variant: "bodySm",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>

                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/payouts"
                        selected={isActivePage("/payouts")}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          pl: 7,
                          "&.Mui-selected": {
                            backgroundColor: "action.selected",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <CreditCard
                            size={18}
                            color={colors.text.primary}
                            strokeWidth={1.5}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("navigation.payouts")}
                          primaryTypographyProps={{
                            variant: "bodySm",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/financials"
                        selected={isActivePage("/financials")}
                        sx={{
                          borderRadius: 2,
                          pl: 7,
                          py: 0.75,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TrendingUp size={18} />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("navigation.financials")}
                          primaryTypographyProps={{
                            variant: "bodySm",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>
              </>
            )}

            {currentUserRole === "owner" && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/settings"
                  selected={isActivePage("/settings")}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    "&.Mui-selected": {
                      backgroundColor: "action.selected",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Settings
                      size={20}
                      color={colors.text.primary}
                      strokeWidth={1.5}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("navigation.settings")}
                    primaryTypographyProps={{
                      variant: "bodySm",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>

          <SidebarAssetList />
        </Box>

        <Typography
          variant="bodySm"
          component="p"
          sx={{
            color: "text.secondary",
            fontSize: "10px",
            textAlign: "center",
            paddingTop: "8px",
            paddingBottom: "20px",
            width: "100%",
            borderTop: "1px solid",
            borderColor: "divider",
            marginTop: "auto",
          }}
        >
          © 2025 RoyaltyX. v1.0
          <br />
          <span style={{ fontSize: "0.6rem", opacity: 0.5 }}>
            Analytics may include estimates based on available data. Not
            financial advice.
          </span>
        </Typography>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={sidebarOpen}
        onClose={closeSidebar}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
            backgroundColor: colors.glass?.background || colors.pageSecondary,
            backdropFilter: colors.glass?.blur
              ? `blur(${colors.glass.blur}) saturate(180%)`
              : "none",
            borderRight: `1px solid ${colors.glass?.border || colors.border.primary}`,
            backgroundImage: `linear-gradient(180deg, ${muiTheme.palette.primary.main}14 0%, ${muiTheme.palette.primary.main}08 40%, ${muiTheme.palette.primary.main}05 100%)`,
          },
        }}
      >
        <SidebarContent />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={sidebarOpen}
      sx={{
        width: sidebarOpen ? SIDEBAR_WIDTH : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: SIDEBAR_WIDTH,
          minWidth: SIDEBAR_WIDTH,
          maxWidth: SIDEBAR_WIDTH,
          boxSizing: "border-box",
          overflow: "hidden",
          overflowY: "auto",
          backgroundColor: colors.glass?.background || colors.pageSecondary,
          backdropFilter: colors.glass?.blur
            ? `blur(${colors.glass.blur}) saturate(180%)`
            : "none",
          borderRight: `1px solid ${colors.glass?.border || colors.border.primary}`,
          backgroundImage: `linear-gradient(180deg, ${muiTheme.palette.primary.main}14 0%, ${muiTheme.palette.primary.main}08 40%, ${muiTheme.palette.primary.main}05 100%)`,
        },
      }}
    >
      <SidebarContent />
    </Drawer>
  );
}

export default Sidebar;
